import { useState } from "react";
import QuoteCarousel from "@/components/QuoteCarousel";
import { LogoMark } from "@/components/Logo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { UserButton } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Download, CheckCircle2, Clock, FileText, AlertCircle,
  RefreshCw, FileDown, FileBadge, AlertTriangle, ShieldAlert, Loader2, Mail,
  Copy, Check, UserX, UserCheck, Upload, Search, Send,
} from "lucide-react";
import { useRef } from "react";


// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n: number | string | null | undefined): string {
  if (n == null) return "$0.00";
  const v = typeof n === "string" ? parseFloat(n) : n;
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

// ── Types ────────────────────────────────────────────────────────────────────

interface Filing1099CRecord {
  letterId: number;
  debtId: number | null;
  userId: number | null;
  pdfUrl: string | null;
  sentAt: string | null;
  createdAt: string | null;
  debtorName: string | null;
  debtorEmail: string | null;
  creditor: string | null;
  balance: string | null;
}

interface TinDebtorRow {
  letterId: number;
  debtId: number | null;
  userId: number | null;
  debtorName: string | null;
  debtorEmail: string | null;
  creditor: string | null;
  balance: string | null;
  tinProvided: boolean;
  tinProvidedAt: string | null;
}

interface Filing {
  id: number;
  year: number;
  filingType: string;
  status: string;
  filingCount: number | null;
  totalAmount: string | null;
  filedAt: string | null;
  notes: string | null;
}

// ── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string }> = {
    pending:  { label: "Pending",  className: "bg-yellow-100 text-yellow-800 border-yellow-300" },
    filed:    { label: "Filed",    className: "bg-blue-100   text-blue-800   border-blue-300"   },
    accepted: { label: "Accepted", className: "bg-green-100  text-green-800  border-green-300"  },
    rejected: { label: "Rejected", className: "bg-red-100    text-red-800    border-red-300"    },
  };
  const cfg = map[status] ?? { label: status, className: "bg-gray-100 text-gray-800" };
  return (
    <Badge variant="outline" className={`text-xs font-semibold ${cfg.className}`}>
      {cfg.label}
    </Badge>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function TaxCenter() {
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [year, setYear] = useState(currentYear);
  const [markFiledNotes, setMarkFiledNotes] = useState("");
  const [downloadingFire, setDownloadingFire] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingAck, setDownloadingAck] = useState(false);
  const [tinReminderResult, setTinReminderResult] = useState<{ sent: number; message: string } | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);
  const [tinMatchingExporting, setTinMatchingExporting] = useState(false);
  const [tinMatchingImporting, setTinMatchingImporting] = useState(false);
  const [tinMatchingImportResult, setTinMatchingImportResult] = useState<{
    mismatchCount: number; notFoundCount: number; linesProcessed: number; message: string;
  } | null>(null);
  const [tinMatchingImportError, setTinMatchingImportError] = useState<string | null>(null);
  const tinMatchingFileRef = useRef<HTMLInputElement>(null);

  // ── Queries ────────────────────────────────────────────────────────────────

  const filingsSummaryQ = useQuery({
    queryKey: ["tax-filings-summary"],
    queryFn: async () => {
      const r = await fetch("api/admin/tax-filings", { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<{ filings: Filing[]; pending1099cByYear: { year: number; count: number }[] }>;
    },
    refetchInterval: 30_000,
  });

  const records1099Q = useQuery({
    queryKey: ["tax-filings-1099c", year],
    queryFn: async () => {
      const r = await fetch(`api/admin/tax-filings/1099c?year=${year}`, { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<{ year: number; count: number; totalAmount: number; records: Filing1099CRecord[] }>;
    },
  });

  const tinStatusQ = useQuery({
    queryKey: ["tin-status-admin", year],
    queryFn: async () => {
      const r = await fetch(`api/admin/tax-filings/1099c/tin-status?year=${year}`, { credentials: "include" });
      if (!r.ok) return null;
      return r.json() as Promise<{
        year: number;
        total: number;
        missingTin: number;
        hasTin: number;
        readyForExport: boolean;
        debtors: TinDebtorRow[];
      }>;
    },
    refetchInterval: 30_000,
  });

  const tinMatchingStatusQ = useQuery({
    queryKey: ["tin-matching-status"],
    queryFn: async () => {
      const r = await fetch("api/admin/tin-matching/status", { credentials: "include" });
      if (!r.ok) return null;
      return r.json() as Promise<{
        debtorCount: number;
        lastExportedAt: string | null;
        lastImportedAt: string | null;
        lastImportMismatchCount: number | null;
      }>;
    },
    refetchInterval: 30_000,
  });

  // ── TIN Reminder Mutation ──────────────────────────────────────────────────

  const tinReminderMut = useMutation({
    mutationFn: async () => {
      const r = await fetch("api/admin/tax-filings/1099c/send-tin-reminders", {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<{ sent: number; message: string }>;
    },
    onSuccess: (data) => {
      setTinReminderResult(data);
      void qc.invalidateQueries({ queryKey: ["tin-status-admin", year] });
    },
  });

  // ── Per-Debtor TIN Reminder ────────────────────────────────────────────────

  const [sentTinUserId, setSentTinUserId] = useState<number | null>(null);

  const singleTinReminderMut = useMutation({
    mutationFn: async (userId: number) => {
      const r = await fetch("api/admin/tax-filings/1099c/send-tin-reminder-single", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: "Failed" })) as { error: string };
        throw new Error(body.error ?? "Failed to send reminder");
      }
      return r.json() as Promise<{ sent: boolean; message: string }>;
    },
    onSuccess: (_, userId) => {
      setSentTinUserId(userId);
      setTimeout(() => setSentTinUserId(null), 3000);
    },
    onError: (err: Error) => {
      toast({ title: "Failed to send reminder", description: err.message, variant: "destructive" });
    },
  });

  // ── Mark Filed Mutation ────────────────────────────────────────────────────

  const markFiledMut = useMutation({
    mutationFn: async () => {
      const r = await fetch("api/admin/tax-filings/1099c/mark-filed", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year, notes: markFiledNotes || undefined }),
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tax-filings-summary"] });
      void qc.invalidateQueries({ queryKey: ["tax-filings-1099c", year] });
      setMarkFiledNotes("");
    },
  });

  // ── FIRE Export Download ──────────────────────────────────────────────────

  async function handleFireExport() {
    setDownloadingFire(true);
    setDownloadError(null);
    try {
      const r = await fetch(`api/admin/tax-filings/1099c/fire-export?year=${year}`, {
        credentials: "include",
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: "Download failed" })) as { error: string };
        throw new Error(body.error ?? "Download failed");
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `1099c-${year}-fire.txt`;
      a.click();
      URL.revokeObjectURL(url);
      void qc.invalidateQueries({ queryKey: ["tax-filings-summary"] });
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloadingFire(false);
    }
  }

  // ── Acknowledgment Letter Bulk Download ───────────────────────────────────

  async function handleAckExport() {
    setDownloadingAck(true);
    try {
      const r = await fetch(`api/admin/donations/letters/zip?year=${year}`, {
        credentials: "include",
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: "Download failed" })) as { error: string };
        throw new Error(body.error ?? "Download failed");
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `irs-acknowledgments-${year}-all-donors.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloadingAck(false);
    }
  }

  // ── Copy Email Helper ─────────────────────────────────────────────────────

  function handleCopyEmail(email: string) {
    void navigator.clipboard.writeText(email).then(() => {
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000);
    });
  }

  // ── TIN Matching Export ───────────────────────────────────────────────────

  async function handleTinMatchingExport() {
    setTinMatchingExporting(true);
    try {
      const r = await fetch("api/admin/tin-matching/export", { credentials: "include" });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: "Export failed" })) as { error: string };
        throw new Error(body.error ?? "Export failed");
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `tin-matching-export-${today}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      void qc.invalidateQueries({ queryKey: ["tin-matching-status"] });
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setTinMatchingExporting(false);
    }
  }

  // ── TIN Matching Import ───────────────────────────────────────────────────

  async function handleTinMatchingImport(file: File) {
    setTinMatchingImporting(true);
    setTinMatchingImportResult(null);
    setTinMatchingImportError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await fetch("api/admin/tin-matching/import", {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({ error: "Import failed" })) as { error: string };
        throw new Error(body.error ?? "Import failed");
      }
      const data = await r.json() as { mismatchCount: number; notFoundCount: number; linesProcessed: number; message: string };
      setTinMatchingImportResult(data);
      void qc.invalidateQueries({ queryKey: ["tin-matching-status"] });
      void qc.invalidateQueries({ queryKey: ["compliance-alerts"] });
    } catch (err) {
      setTinMatchingImportError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setTinMatchingImporting(false);
      if (tinMatchingFileRef.current) tinMatchingFileRef.current.value = "";
    }
  }

  // ── Derived Data ──────────────────────────────────────────────────────────

  const yearFiling = filingsSummaryQ.data?.filings.find(
    (f) => f.year === year && f.filingType === "form_1099c"
  );
  const records = records1099Q.data?.records ?? [];
  const totalAmount = records1099Q.data?.totalAmount ?? 0;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b bg-slate-900 border-slate-700">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <LogoMark size={26} />
            <span className="text-sm font-semibold text-white">IRS Tax Center</span>
            <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white tracking-wide">ADMIN</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/admin/form-990")}>
              <FileBadge className="h-4 w-4 mr-1" /> Form 990-EZ
            </Button>
            <UserButton />
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">IRS 1099-C Tax Center</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage IRS Form 1099-C (Cancellation of Debt) filings. Download IRS FIRE-compatible
              upload files and track filing status.
            </p>
          </div>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="rounded-lg border p-4 space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">1099-Cs Generated</p>
            <p className="text-2xl font-bold">{records.length}</p>
          </div>
          <div className="rounded-lg border p-4 space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Cancelled</p>
            <p className="text-2xl font-bold">{fmt(totalAmount)}</p>
          </div>
          <div className="rounded-lg border p-4 space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">IRS Filing Status</p>
            <div className="pt-1">
              {yearFiling ? (
                <StatusBadge status={yearFiling.status} />
              ) : (
                <Badge variant="outline" className="bg-gray-100 text-gray-600 text-xs">Not Started</Badge>
              )}
            </div>
          </div>
          <div className="rounded-lg border p-4 space-y-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Date Filed</p>
            <p className="text-sm font-semibold">{shortDate(yearFiling?.filedAt)}</p>
          </div>
        </div>

        {/* IRS Compliance Notice */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 space-y-1">
            <p className="font-semibold">IRS Compliance Reminder</p>
            <p>
              Form 1099-C must be filed with the IRS and furnished to recipients by <strong>January 31</strong> for
              debts cancelled in the prior tax year (IRC §6050P). Upload the FIRE file at{" "}
              <a
                href="https://fire.irs.gov"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                fire.irs.gov
              </a>{" "}
              after reviewing all records. All payees must submit their SSN/ITIN before the FIRE file can be generated.
            </p>
          </div>
        </div>

        {/* TIN Status Warning (year-scoped) */}
        {tinStatusQ.data && tinStatusQ.data.total > 0 && (
          tinStatusQ.data.missingTin > 0 ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex gap-3">
              <ShieldAlert className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="text-sm text-red-900">
                <p className="font-semibold">
                  {tinStatusQ.data.missingTin} of {tinStatusQ.data.total} payee{tinStatusQ.data.total !== 1 ? "s" : ""} missing SSN/ITIN for {year}
                </p>
                <p className="mt-0.5 text-red-800">
                  FIRE export is blocked until all debtors provide their Tax ID. Use the TIN Reminder Blast section below to notify them.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
              <div className="text-sm text-green-900">
                <p className="font-semibold">All {tinStatusQ.data.total} payee{tinStatusQ.data.total !== 1 ? "s" : ""} have provided their SSN/ITIN</p>
                <p className="mt-0.5 text-green-800">The FIRE file is ready for download and submission to the IRS.</p>
              </div>
            </div>
          )
        )}

        {/* TIN Status Breakdown Table */}
        {tinStatusQ.data && tinStatusQ.data.total > 0 && (
          <div className="rounded-lg border">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                TIN Status Breakdown — {year}
              </h2>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <UserCheck className="h-3.5 w-3.5 text-green-600" />
                  {tinStatusQ.data.hasTin} provided
                </span>
                <span className="flex items-center gap-1">
                  <UserX className="h-3.5 w-3.5 text-red-500" />
                  {tinStatusQ.data.missingTin} missing
                </span>
                {tinStatusQ.isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="text-xs uppercase text-muted-foreground">
                  <TableHead>Debt #</TableHead>
                  <TableHead>Debtor</TableHead>
                  <TableHead>Creditor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>TIN Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tinStatusQ.data.debtors.map((row) => (
                  <TableRow key={row.letterId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      #{String(row.debtId ?? 0).padStart(8, "0")}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{row.debtorName ?? "—"}</div>
                      {row.debtorEmail ? (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-xs text-muted-foreground">{row.debtorEmail}</span>
                          <button
                            type="button"
                            title="Copy email"
                            onClick={() => handleCopyEmail(row.debtorEmail!)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {copiedEmail === row.debtorEmail ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground">—</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{row.creditor ?? "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-sm">
                      {fmt(row.balance)}
                    </TableCell>
                    <TableCell>
                      {row.tinProvided ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium">
                          <UserCheck className="h-3.5 w-3.5" />
                          Provided
                          {row.tinProvidedAt && (
                            <span className="text-muted-foreground font-normal ml-0.5">
                              {shortDate(row.tinProvidedAt)}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600 font-semibold">
                          <UserX className="h-3.5 w-3.5" />
                          Missing
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {!row.tinProvided && row.userId != null && (
                        sentTinUserId === row.userId ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium">
                            <Check className="h-3.5 w-3.5" /> Sent
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs gap-1"
                            disabled={singleTinReminderMut.isPending && singleTinReminderMut.variables === row.userId}
                            onClick={() => singleTinReminderMut.mutate(row.userId!)}
                          >
                            {singleTinReminderMut.isPending && singleTinReminderMut.variables === row.userId ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Send className="h-3.5 w-3.5" />
                            )}
                            Remind
                          </Button>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => void handleFireExport()}
            disabled={downloadingFire || records.length === 0 || (tinStatusQ.data != null && tinStatusQ.data.missingTin > 0)}
            className="gap-2"
            title={tinStatusQ.data?.missingTin ? `${tinStatusQ.data.missingTin} payee(s) missing TIN` : undefined}
          >
            {downloadingFire ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            Download IRS FIRE File ({year})
          </Button>

          <Button
            variant="outline"
            onClick={() => markFiledMut.mutate()}
            disabled={markFiledMut.isPending || records.length === 0}
            className="gap-2"
          >
            {markFiledMut.isPending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            Mark as Filed with IRS
          </Button>

          {yearFiling?.status === "filed" && (
            <Badge className="bg-green-100 text-green-800 border border-green-300 gap-1 px-3 py-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Filed {shortDate(yearFiling.filedAt)}
            </Badge>
          )}
        </div>

        {downloadError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex gap-2 text-sm text-red-800">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            {downloadError}
          </div>
        )}

        {markFiledMut.isSuccess && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 flex gap-2 text-sm text-green-800">
            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
            Batch marked as filed with IRS for tax year {year}.
          </div>
        )}

        {/* 1099-C Records Table */}
        <div className="rounded-lg border">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4" />
              1099-C Records — {year}
            </h2>
            {records1099Q.isFetching && <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>

          {records1099Q.isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Loading records…</div>
          ) : records.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No 1099-C records for {year}. Records are auto-generated when debts ≥ $600 are cleared.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="text-xs uppercase text-muted-foreground">
                  <TableHead>Debt #</TableHead>
                  <TableHead>Debtor</TableHead>
                  <TableHead>Creditor</TableHead>
                  <TableHead className="text-right">Amount Cancelled</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((r) => (
                  <TableRow key={r.letterId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      #{String(r.debtId ?? 0).padStart(8, "0")}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{r.debtorName ?? "—"}</div>
                      <div className="text-xs text-muted-foreground">{r.debtorEmail ?? "—"}</div>
                    </TableCell>
                    <TableCell className="text-sm">{r.creditor ?? "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-sm">
                      {fmt(r.balance)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.sentAt ? (
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                          {shortDate(r.sentAt)}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Clock className="h-3.5 w-3.5" />
                          Pending
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.pdfUrl ? (
                        <a
                          href={r.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <Download className="h-3 w-3" />
                          PDF
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* TIN Reminder Blast — global (not year-scoped) */}
        <div className="rounded-lg border p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <Mail className="h-4 w-4" />
              TIN Reminder Blast — All Years
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Send a one-click email reminder to every debtor with a cleared debt who has not yet provided their SSN/ITIN.
              This covers all tax years, not just the selected year above.
            </p>
            {tinReminderResult && (
              <p className="mt-2 text-sm font-medium text-green-700 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />
                {tinReminderResult.message}
              </p>
            )}
            {tinReminderMut.isError && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4" />
                Failed to send reminders — please try again.
              </p>
            )}
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-shrink-0 gap-1.5"
                disabled={tinReminderMut.isPending}
              >
                {tinReminderMut.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  <><Mail className="h-4 w-4" /> Send TIN Reminders</>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Send TIN reminder emails?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will send an email to every debtor with a cleared debt who has not yet provided their SSN/ITIN,
                  across all tax years. Each debtor will receive one email. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => { setTinReminderResult(null); tinReminderMut.mutate(); }}
                >
                  Send Reminders
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {/* IRS TIN Matching */}
        <div className="rounded-lg border p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <Search className="h-4 w-4" />
                IRS TIN Matching
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Verify debtors' SSNs/ITINs against the IRS database before filing 1099-Cs.
                Export a file, submit it to the IRS TIN Matching program, then import the results to flag mismatches.
              </p>
            </div>
            {tinMatchingStatusQ.data != null && (
              <div className="text-xs text-muted-foreground text-right shrink-0 space-y-0.5">
                <p>
                  <span className="font-medium text-foreground">{tinMatchingStatusQ.data.debtorCount}</span> debtor{tinMatchingStatusQ.data.debtorCount !== 1 ? "s" : ""} with TIN on file
                </p>
                {tinMatchingStatusQ.data.lastExportedAt && (
                  <p>Last exported: {new Date(tinMatchingStatusQ.data.lastExportedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
                )}
                {tinMatchingStatusQ.data.lastImportedAt && (
                  <p>Last imported: {new Date(tinMatchingStatusQ.data.lastImportedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    {tinMatchingStatusQ.data.lastImportMismatchCount != null && (
                      <span className={tinMatchingStatusQ.data.lastImportMismatchCount > 0 ? " text-red-600 font-medium" : " text-green-600 font-medium"}>
                        {" "}({tinMatchingStatusQ.data.lastImportMismatchCount} mismatch{tinMatchingStatusQ.data.lastImportMismatchCount !== 1 ? "es" : ""})
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 space-y-1">
            <p className="font-semibold">How to use the IRS TIN Matching program:</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Click <strong>Export for IRS TIN Matching</strong> to download a pipe-delimited .txt file</li>
              <li>Log in at <a href="https://www.irs.gov/filing/e-file-providers/tin-matching" target="_blank" rel="noopener noreferrer" className="underline">irs.gov/filing/e-file-providers/tin-matching</a> using your IRS e-Services credentials</li>
              <li>Select <strong>Bulk TIN Matching</strong> and upload the file (results are usually ready within 24 hours)</li>
              <li>Download the IRS results file (same format + a result code appended to each row)</li>
              <li>Click <strong>Import Results</strong> below and upload the IRS response file</li>
              <li>Any mismatches will appear as <strong>TIN Mismatch</strong> alerts in Compliance Alerts</li>
            </ol>
          </div>

          {/* Export + Import actions */}
          <div className="flex flex-wrap gap-3 items-start">
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => void handleTinMatchingExport()}
              disabled={tinMatchingExporting || (tinMatchingStatusQ.data?.debtorCount ?? 0) === 0}
              title={tinMatchingStatusQ.data?.debtorCount === 0 ? "No debtors with a TIN on file" : undefined}
            >
              {tinMatchingExporting ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Exporting…</>
              ) : (
                <><FileDown className="h-4 w-4" /> Export for IRS TIN Matching</>
              )}
            </Button>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="tin-matching-import"
                className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border font-medium transition-colors cursor-pointer
                  ${tinMatchingImporting ? "opacity-60 pointer-events-none" : "hover:bg-muted border-input bg-background"}`}
              >
                {tinMatchingImporting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Importing…</>
                ) : (
                  <><Upload className="h-4 w-4" /> Import Results</>
                )}
              </label>
              <input
                id="tin-matching-import"
                ref={tinMatchingFileRef}
                type="file"
                accept=".txt,text/plain"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleTinMatchingImport(file);
                }}
              />
              <p className="text-xs text-muted-foreground">Upload the IRS response .txt file</p>
            </div>
          </div>

          {tinMatchingImportResult && (
            <div className={`rounded-md border p-3 text-sm flex items-start gap-2 ${
              tinMatchingImportResult.mismatchCount > 0
                ? "border-amber-200 bg-amber-50 text-amber-900"
                : "border-green-200 bg-green-50 text-green-900"
            }`}>
              {tinMatchingImportResult.mismatchCount > 0
                ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                : <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-green-600" />
              }
              <div>
                <p className="font-medium">{tinMatchingImportResult.message}</p>
                <p className="text-xs mt-0.5 opacity-80">
                  {tinMatchingImportResult.linesProcessed} flagged row{tinMatchingImportResult.linesProcessed !== 1 ? "s" : ""} processed
                  {tinMatchingImportResult.notFoundCount > 0 && ` · ${tinMatchingImportResult.notFoundCount} could not be matched to a debtor account`}
                  {tinMatchingImportResult.mismatchCount > 0 && " · Check Compliance Alerts for details"}
                </p>
              </div>
            </div>
          )}

          {tinMatchingImportError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm flex gap-2 text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              {tinMatchingImportError}
            </div>
          )}
        </div>

        {/* Bulk Acknowledgment Letter Download */}
        <div className="rounded-lg border p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-sm font-semibold flex items-center gap-2">
              <FileDown className="h-4 w-4" />
              Donor Acknowledgment Letters — {year}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Download all IRS 501(c)(3) acknowledgment letters sent to donors for tax year {year} as a single ZIP file.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={downloadingAck}
            onClick={() => void handleAckExport()}
            className="flex-shrink-0"
          >
            {downloadingAck
              ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Preparing…</>
              : <><Download className="h-4 w-4 mr-1.5" /> Download ZIP ({year})</>
            }
          </Button>
        </div>

        {/* All Years Summary */}
        {filingsSummaryQ.data?.filings && filingsSummaryQ.data.filings.length > 0 && (
          <div className="rounded-lg border">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-semibold">All Tax Filing Records</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="text-xs uppercase text-muted-foreground">
                  <TableHead>Year</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Count</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Filed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filingsSummaryQ.data.filings.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className="font-semibold">{f.year}</TableCell>
                    <TableCell className="text-xs font-mono uppercase">{f.filingType}</TableCell>
                    <TableCell>{f.filingCount ?? "—"}</TableCell>
                    <TableCell>{fmt(f.totalAmount)}</TableCell>
                    <TableCell><StatusBadge status={f.status} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{shortDate(f.filedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <QuoteCarousel />

    </div>
  );
}
